import { useEffect, useState } from 'react'
import {Plus, AlertTriangle, ShieldCheck} from 'lucide-react'
import EmpresaLayout from '@/components/EmpresaLayout'
import CaseReportModal from '@/components/CaseReportModal'
import CaseList from '@/components/CaseList'
import { lumi } from '@/lib/lumi'
import { useAuth } from '@/hooks/useAuth'
import { useCases, type WorkCase } from '@/hooks/useCases'

function EmpresaCasos() {
  const { user } = useAuth()
  const { cases, loading, fetchByReporter, createCase } = useCases()
  const [companyName, setCompanyName] = useState('')
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    if (user) {
      fetchByReporter(user.userId)
      lumi.entities.companies
        .list({ filter: { userId: user.userId }, limit: 1 })
        .then((r) => {
          if (r.list.length > 0) setCompanyName(r.list[0].tradeName || '')
        })
        .catch((e) => console.error('Error al cargar empresa:', e))
    }
  }, [user])

  const handleSubmit = async (data: Partial<WorkCase>) => {
    await createCase(data)
    if (user) fetchByReporter(user.userId)
  }

  const validated = cases.filter((c) => c.status === 'validado').length

  return (
    <EmpresaLayout title="Reportes laborales" subtitle="Reporta conductas graves de colaboradores para revisión de Beeznz Work">
      <div className="mb-6 grid gap-3 sm:grid-cols-3">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500">Casos abiertos</p>
          <p className="mt-1 text-2xl font-bold text-slate-900">{cases.length}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500">Validados</p>
          <p className="mt-1 text-2xl font-bold text-emerald-600">{validated}</p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs text-slate-500">En proceso</p>
          <p className="mt-1 text-2xl font-bold text-amber-600">
            {cases.filter((c) => c.status === 'abierto' || c.status === 'en_revision').length}
          </p>
        </div>
      </div>

      <div className="mb-6 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
        <ShieldCheck size={20} className="mt-0.5 shrink-0 text-amber-600" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-amber-900">Proceso de validación</p>
          <p className="mt-0.5 text-xs text-amber-700">
            Cada caso es revisado por el equipo de Beeznz Work. Solo los casos validados se integran al historial laboral del colaborador.
          </p>
        </div>
        <button
          onClick={() => setShowModal(true)}
          className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-amber-400 px-4 py-2 text-sm font-semibold text-slate-900 hover:bg-amber-300"
        >
          <Plus size={15} /> Abrir caso
        </button>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <AlertTriangle size={18} className="text-amber-500" />
        <h2 className="text-base font-semibold text-slate-900">Mis casos reportados</h2>
      </div>
      <CaseList cases={cases} loading={loading} emptyText="Aún no has reportado ningún caso." />

      {showModal && user && (
        <CaseReportModal
          reporterRole="empresa"
          reporterUserId={user.userId}
          reporterName={companyName || user.email}
          onClose={() => setShowModal(false)}
          onSubmit={handleSubmit}
        />
      )}
    </EmpresaLayout>
  )
}

export default EmpresaCasos
